import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useAccessCatalogue, useAccessGroups, useCreateAccessGroup } from '../../hooks';
import { formatGroupPath, groupParentOptions } from '../../lib/groupHierarchy';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Preselects the parent department when the dialog is opened from a row's "Add sub-department"
   * action. `undefined` (the top "New group" button, or the empty state) leaves it at top level.
   */
  initialParentId?: string;
}

/**
 * The "top level" sentinel for the parent selector. Deliberately NOT `''`: MUI's `Select` treats an
 * empty-string value as its own "nothing selected" state and never displays a matching `MenuItem`'s
 * label for it (see `Select`'s `value` prop doc — "Providing an empty string will select no options"),
 * so a real, non-empty sentinel is what makes "Top level (no parent)" actually render as selected.
 */
const TOP_LEVEL_VALUE = '__top-level__';

/**
 * Creating a group starts from a platform role template.
 *
 * <p>The template is <b>copied</b>, not linked (adr-merchant-portal-011): the merchant may then tune the
 * copy freely, and a later platform change to the template never silently widens anyone's access. Starting
 * from a blank group is allowed but deliberately not the default — an empty matrix is twenty decisions,
 * and administrators asked to make twenty decisions tend to grant everything.
 *
 * <p>A group can also be created as a sub-department of an existing one (adr-merchant-portal-016): its
 * members then inherit that parent's grants too, on top of whatever this dialog seeds it with. The
 * parent selector shows each candidate by its `path`, not its bare name — group names are unique only
 * among siblings, so two different "Managers" groups must stay distinguishable in the list.
 */
export function CreateGroupDialog({ open, onClose, initialParentId }: CreateGroupDialogProps) {
  const { t } = useTranslation('access');
  const catalogue = useAccessCatalogue();
  const groups = useAccessGroups();
  const create = useCreateAccessGroup();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sourceTemplate, setSourceTemplate] = useState('');
  const [parentId, setParentId] = useState(initialParentId ?? TOP_LEVEL_VALUE);

  // The dialog stays mounted throughout (only `open` toggles), so a plain `useState` initializer would
  // only ever see the FIRST `initialParentId` it was given. Re-syncing on every open means a fresh
  // "Add sub-department" click always preselects that row's group, not a stale previous one.
  useEffect(() => {
    if (open) setParentId(initialParentId ?? TOP_LEVEL_VALUE);
  }, [open, initialParentId]);

  const parentOptions = groupParentOptions(groups.data ?? []);

  const close = () => {
    create.reset();
    setName('');
    setDescription('');
    setSourceTemplate('');
    setParentId(TOP_LEVEL_VALUE);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{t('createGroup.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {create.isError && <Alert severity="error">{create.error?.message}</Alert>}
          <TextField
            label={t('createGroup.nameLabel')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label={t('createGroup.descriptionLabel')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
          />
          <TextField
            select
            label={t('createGroup.parentLabel')}
            value={parentId}
            onChange={(e) => setParentId(e.target.value)}
            helperText={t('createGroup.parentHelper')}
            fullWidth
          >
            <MenuItem value={TOP_LEVEL_VALUE}>{t('createGroup.noParent')}</MenuItem>
            {parentOptions.map((option) => (
              <MenuItem key={option.id} value={option.id} sx={{ pl: 2 + (option.depth ?? 0) * 2 }}>
                {formatGroupPath(option)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('createGroup.templateLabel')}
            value={sourceTemplate}
            onChange={(e) => setSourceTemplate(e.target.value)}
            helperText={t('createGroup.templateHelper')}
            fullWidth
          >
            <MenuItem value="">{t('createGroup.noTemplate')}</MenuItem>
            {(catalogue.data?.templates ?? []).map((template) => (
              <MenuItem key={template.code} value={template.code}>
                {t(`templates.${template.code}.label`, template.code)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={create.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={name.trim().length === 0 || create.isPending}
          onClick={() =>
            create.mutate(
              {
                name: name.trim(),
                description: description.trim() || undefined,
                sourceTemplate: sourceTemplate || undefined,
                parentId: parentId === TOP_LEVEL_VALUE ? undefined : parentId,
              },
              { onSuccess: close },
            )
          }
        >
          {t('createGroup.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
